import React, { useState, useEffect, ReactNode } from "react";
import axios from "axios";
import { logError } from "../utils/describeError";
import MergeGameDataContext, {
  MergeGameDataContextType,
} from "./MergeGameDataContext";
import { Order, BoardItem } from "../Models/Types";

import { getAccessToken } from "../Components/Utils/TokenRefresh";
import { GRID, BOARD_SIZE } from "../Constants/grid";
import { useAccessToken } from "../Components/Hooks/useAccessToken";

export const MergeGameDataProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [boardItems, setBoardItems] = useState<(BoardItem | null)[]>(
    Array(BOARD_SIZE).fill(null)
  );
  const [pendingOrders, setPendingOrders] = useState<Order[]>([]);
  const gridSize = GRID;
  const initialItems = new Array(gridSize.rows * gridSize.cols).fill(null);
  const [itemHierarchies, setItemHierarchies] = useState<any>(null);
  const [allProducersDefs, setAllProducersDefs] = useState<any[]>([]);

  const [playerLevel, setPlayerLevel] = useState(1);
  const [playerXP, setPlayerXP] = useState(0);
  const [xpForNext, setXpForNext] = useState(100);
  const getToken = useAccessToken();

  // Player stats — the endpoint doesn't exist yet. Skip the call so every
  // page load doesn't log a 404; defaults stay in place.
  const PLAYER_STATS_ENDPOINT_AVAILABLE = false;
  const fetchPlayerStats = async () => {
    if (!PLAYER_STATS_ENDPOINT_AVAILABLE) return;
    try {
      const token = await getAccessToken();
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/mergeGameProgress/playerStats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlayerLevel(data.level ?? 1);
      setPlayerXP(data.xp ?? 0);
      setXpForNext(data.xpForNext ?? 100);
    } catch {
      // Endpoint may not exist yet — use defaults
    }
  };

  // fetchPlayerStats is called in the main mount effect below

  const fetchBoardItems = async () => {
    try {
      const token = await getAccessToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/mergeGameProgress`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // The player level gates the producers that have no planet (Producers panel).
      if (typeof response.data?.playerLevel === "number") {
        setPlayerLevel(response.data.playerLevel);
        if (typeof response.data.playerXP === "number") setPlayerXP(response.data.playerXP);
        if (typeof response.data.xpForNext === "number") setXpForNext(response.data.xpForNext);
      }

      if (response.data && response.data.boardItems) {
        const itemsFromServer = response.data.boardItems;

        const boardItemsArray = new Array(gridSize.rows * gridSize.cols).fill(
          null
        );
        itemsFromServer.filter(Boolean).forEach((item: any) => {
          const boardItem: BoardItem = {
            id: item.id,
            group: item.group,
            type: item.type,
            level: item.level,
            isMax: item.isMax || false,
            url: item.url,
            positionIndex: item.positionIndex,
            set: item.set,
            covered: item.covered,
          };

          boardItemsArray[item.positionIndex] = boardItem;
        });
        setBoardItems(boardItemsArray);
      }
    } catch (error) {
      logError("MergeGameDataProvider: Error fetching board items", error);
      setBoardItems(initialItems);
    }
  };

  const fetchItemHierarchies = async () => {
    try {
      const token = await getAccessToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/mergeGameProgress/hierarchies`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data) {
        setItemHierarchies(response.data);
      }
    } catch (error) {
      logError("MergeGameDataProvider: Error fetching item hierarchies", error);
    }
  };

  const fetchAllProducerDefs = async () => {
    try {
      const token = await getAccessToken();
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/mergeGameProgress/allProducersDefs`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllProducersDefs(data);
    } catch (error) {
      logError("MergeGameDataProvider: Error fetching allProducersDefs", error);
    }
  };

  const fetchOrders = async () => {
    try {
      const token = await getAccessToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/mergeGameOrders`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data && response.data.pendingOrders) {
        const itemsFromServer = response.data.pendingOrders;
        setPendingOrders(itemsFromServer);
      }
    } catch (error) {
      logError("MergeGameDataProvider: Error fetching orders", error);
    }
  };

  const saveBoardItems = async (updatedBoardItems: (BoardItem | null)[]) => {
    try {
      const token = await getAccessToken();
      const boardItemsToSave = updatedBoardItems
        .map((item, index) => {
          if (item) {
            return {
              ...item,
              positionIndex: index,
            };
          }
          return null;
        })
        .filter((item) => item !== null);

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/mergeGameProgress`,
        { boardItems: boardItemsToSave },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

    } catch (error) {
      logError("MergeGameDataProvider: Error saving board items", error);
    }
  };

  const saveOrders = async (updatedOrders: Order[]) => {
    try {
      const token = await getAccessToken();
      const ordersToSave = updatedOrders
        .map((item, index) => {
          if (item) {
            return {
              ...item,
              positionIndex: index,
            };
          }
          return null;
        })
        .filter((item) => item !== null);

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/mergeGameOrders`,
        { orders: ordersToSave },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

    } catch (error) {
      logError("MergeGameDataProvider: Error saving board items", error);
    }
  };

  useEffect(() => {
    (async () => {
      const token = await getToken();
      if (!token) return;
      fetchBoardItems();
      fetchOrders();
      fetchItemHierarchies();
      fetchAllProducerDefs();
      fetchPlayerStats();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue: MergeGameDataContextType = {
    boardItems,
    setBoardItems,
    fetchBoardItems,
    saveBoardItems,
    gridSize,
    pendingOrders,
    setPendingOrders,
    fetchOrders,
    saveOrders,
    itemHierarchies,
    fetchItemHierarchies,
    playerLevel,
    setPlayerLevel,
    playerXP,
    setPlayerXP,
    xpForNext,
    setXpForNext,
    allProducersDefs,
    setAllProducersDefs,
    fetchAllProducerDefs,
  };

  return (
    <MergeGameDataContext.Provider value={contextValue}>
      {children}
    </MergeGameDataContext.Provider>
  );
};

export default MergeGameDataProvider;
